/**
 * The transactions table: sortable header, rows from <TransactionRow>. Sorting is
 * client-side over the loaded rows — a family's month fits one page, and search +
 * the category/month filters narrow the set server-side before it ever gets here.
 */
import type { BudgetCategory, Transaction } from '../../shared/types';
import TransactionRow from './TransactionRow';

export type SortKey = 'date' | 'description' | 'category' | 'amount';
export type SortDir = 'asc' | 'desc';

interface Props {
  rows: Transaction[];
  categories: BudgetCategory[];
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  onChanged: (t: Transaction) => void;
  onDeleted: (t: Transaction) => void;
  onFlash: (message: string) => void;
  onError: (message: string) => void;
}

function SortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
  align = 'left',
  className = '',
}: {
  label: string;
  col: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
  className?: string;
}) {
  const active = sortKey === col;
  return (
    <th
      scope="col"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-3 py-2.5 font-medium ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 rounded hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50 ${
          active ? 'text-ink' : 'text-ink-soft'
        } ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {label}
        <span aria-hidden="true" className={active ? 'text-sage' : 'text-ink-faint'}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

export default function TransactionTable({
  rows,
  categories,
  sortKey,
  sortDir,
  onSort,
  onChanged,
  onDeleted,
  onFlash,
  onError,
}: Props) {
  const headProps = { sortKey, sortDir, onSort };
  return (
    <div className="overflow-x-auto rounded-card border border-border">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-sunk/50 text-xs uppercase tracking-wide">
            <SortHeader label="Date" col="date" {...headProps} className="whitespace-nowrap" />
            <SortHeader label="Description" col="description" {...headProps} />
            <SortHeader label="Category" col="category" {...headProps} />
            <th scope="col" className="hidden px-3 py-2.5 text-left font-medium text-ink-soft md:table-cell">
              Account
            </th>
            <SortHeader label="Amount" col="amount" align="right" {...headProps} />
            <th scope="col" className="px-3 py-2.5">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <TransactionRow
              key={t.id}
              transaction={t}
              categories={categories}
              onChanged={onChanged}
              onDeleted={onDeleted}
              onFlash={onFlash}
              onError={onError}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
